import { useEffect, useRef } from 'react';

import { Trans } from '@lingui/react/macro';
import { useAtom, useAtomValue } from 'jotai';

import { searchAtom, selectDateAtom } from '../patientList/atoms';
import { PatientListPageLayout } from '../patientList/PatientListPageLayout';
import { PatientListPageLayoutContextMap } from '../patientList/types';

import { AmbientTable } from './AmbientTable';
import {
  useAmbientAppointmentsData,
  useAmbientInfiniteScroll,
  useAmbientSorting,
} from './hooks';

export const AmbientPage = () => {
  const [selectedDate, setSelectedDate] = useAtom(selectDateAtom);
  const search = useAtomValue(searchAtom);

  // Ambient-specific hooks
  const {
    appointmentsWithTreatmentsAndNotes,
    isLoading,
    isError,
    hasNextPage,
    isAnyBatchLoading,
    fetchNextPage,
    invalidateNotesCache,
  } = useAmbientAppointmentsData();

  const { sortedAppointments, sortField, sortDirection, handleSort } =
    useAmbientSorting(appointmentsWithTreatmentsAndNotes);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { ref: sentinelRef } = useAmbientInfiniteScroll(
    hasNextPage,
    isLoading,
    fetchNextPage,
    scrollContainerRef,
  );

  // Set today as default date and reset when search is cleared
  useEffect(() => {
    if (search === '' && !selectedDate) {
      setSelectedDate(new Date());
    }
  }, [search, selectedDate, setSelectedDate]);

  return (
    <PatientListPageLayout
      context={PatientListPageLayoutContextMap.Ambient}
      allAppointments={sortedAppointments}
      isLoadingAppointments={isLoading || isAnyBatchLoading}
      isLoadingVisitSummaries={false}
    >
      <div className="vai-flex vai-flex-col vai-h-[87vh]">
        {isError ? (
          <div className="vai-flex vai-justify-center vai-items-center vai-p-8">
            <div className="vai-text-red-500">
              <Trans>Error loading appointments</Trans>
            </div>
          </div>
        ) : (
          <AmbientTable
            appointments={sortedAppointments}
            onSort={handleSort}
            sortField={sortField}
            sortDirection={sortDirection}
            invalidateNotesCache={invalidateNotesCache}
            scrollContainerRef={scrollContainerRef}
            sentinelRef={sentinelRef}
          />
        )}
      </div>
    </PatientListPageLayout>
  );
};
